import React from "react";
import { formatCurrency } from "../../../helpers/estimateTotals";
import { REPORT_HEADER_SHELL } from "../../../helpers/reportHeader";
import { getPayrollTotalPresentation } from "../../../helpers/payrollReport";
import { PayrollReportTotalSection } from "./PayrollReportTotalSection";
import { PayrollSummaryCards } from "./PayrollSummaryCards";
import { ReportPreviewHeader } from "../../report/ReportPreviewHeader";
import { ReportPrintHeader } from "../../report/ReportPrintHeader";
import { ReportRule } from "../../report/ReportRule";

function PreviewSummaryDetailLine({ row }) {
  return (
    <div className="er-item-row">
      <div className="er-item-name">
        <div>{row.label}</div>
        {row.detail && <span className="er-item-detail">{row.detail}</span>}
      </div>
      <div className="er-item-amount">{formatCurrency(row.amount)}</div>
    </div>
  );
}

function PreviewDetailRow({ row }) {
  return (
    <div className="er-item-row">
      <div className="er-item-name">
        <div>{row.date}</div>
        {row.project && row.project !== "—" && (
          <span className="er-item-detail">{row.project}</span>
        )}
        {row.trade && row.trade !== "—" && (
          <span className="er-item-detail">{row.trade}</span>
        )}
        {row.classification && row.classification !== "—" && (
          <span className="er-item-detail">{row.classification}</span>
        )}
        {row.payrollType && row.payrollType !== "—" && (
          <span className="er-item-detail">{row.payrollType}</span>
        )}
        {row.detail && <span className="er-item-detail">{row.detail}</span>}
      </div>
      <div className="er-item-amount">{formatCurrency(row.gross)}</div>
    </div>
  );
}

function PrintSummaryDetailLine({ row }) {
  return (
    <div className="erp-item-row">
      <div className="erp-item-name">
        <div>{row.label}</div>
        {row.detail && <span className="erp-item-detail">{row.detail}</span>}
      </div>
      <div className="erp-item-amount">{formatCurrency(row.amount)}</div>
    </div>
  );
}

function PrintDetailTable({ rows, options }) {
  if (!rows?.length) return null;

  const showProject = options?.showProjectNames;
  const showCategories = options?.showWorkCategories;
  const showTrade = options?.showTrade;
  const showBreaks = options?.showBreaks;
  const showRates = options?.showHourlyRates;

  return (
    <table className="prp-detail-table">
      <thead>
        <tr>
          <th className="prp-detail-table__date">Date</th>
          {showProject && (
            <th className="prp-detail-table__project">Project</th>
          )}
          {showTrade && <th className="prp-detail-table__trade">Trade</th>}
          {showCategories && (
            <th className="prp-detail-table__classification">Classification</th>
          )}
          {showCategories && (
            <th className="prp-detail-table__payroll-type">Payroll Type</th>
          )}
          <th className="prp-detail-table__hours">Hours</th>
          {showBreaks && <th className="prp-detail-table__break">Break</th>}
          {showRates && <th className="prp-detail-table__rate">Rate</th>}
          <th className="prp-detail-table__gross">Gross</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id || `${row.date}-${row.project}`}>
            <td className="prp-detail-table__date">{row.date}</td>
            {showProject && (
              <td className="prp-detail-table__project">{row.project || "—"}</td>
            )}
            {showTrade && (
              <td className="prp-detail-table__trade">{row.trade || "—"}</td>
            )}
            {showCategories && (
              <td className="prp-detail-table__classification">
                {row.classification || "—"}
              </td>
            )}
            {showCategories && (
              <td className="prp-detail-table__payroll-type">
                {row.payrollType || "—"}
              </td>
            )}
            <td className="prp-detail-table__hours">
              {Number(row.hours || 0).toFixed(2)}
            </td>
            {showBreaks && (
              <td className="prp-detail-table__break">
                {row.breakHours != null
                  ? Number(row.breakHours).toFixed(2)
                  : "—"}
              </td>
            )}
            {showRates && (
              <td className="prp-detail-table__rate">
                {row.rate != null ? formatCurrency(row.rate) : "—"}
              </td>
            )}
            <td className="prp-detail-table__gross">
              {formatCurrency(row.gross || 0)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PreviewDetailSection({ detail }) {
  if (!detail || detail.mode === "hidden") return null;

  return (
    <>
      <ReportRule variant="preview" />
      <h2 className="er-section-title">{detail.summaryLabel}</h2>
      {detail.mode === "summary" &&
        detail.summaryRows.map((row, index) => (
          <PreviewSummaryDetailLine key={`${row.label}-${index}`} row={row} />
        ))}
      {detail.mode === "detailed" &&
        detail.detailRows.map((row) => (
          <PreviewDetailRow key={row.id || `${row.date}-${row.project}`} row={row} />
        ))}
    </>
  );
}

function PrintDetailSection({ detail, options }) {
  if (!detail || detail.mode === "hidden") return null;

  return (
    <section className="erp-section erp-break-avoid">
      <ReportRule />
      <h2 className="erp-section-title">{detail.summaryLabel}</h2>
      {detail.mode === "summary" &&
        detail.summaryRows.map((row, index) => (
          <PrintSummaryDetailLine key={`${row.label}-${index}`} row={row} />
        ))}
      {detail.mode === "detailed" && (
        <PrintDetailTable rows={detail.detailRows} options={options} />
      )}
    </section>
  );
}

export function PayrollReportEmployeeSection({
  sectionModel,
  variant = "preview",
  className = "",
}) {
  if (!sectionModel) return null;

  const { header, summary, detail, options } = sectionModel;
  const totalPresentation = getPayrollTotalPresentation(sectionModel);
  const isPrint = variant === "print";
  const sectionClassName = [
    isPrint ? "payroll-report-employee-section" : "payroll-report-employee-section",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (isPrint) {
    return (
      <div className={sectionClassName}>
        <ReportPrintHeader
          {...REPORT_HEADER_SHELL.payroll}
          fields={header.fields}
        />

        {summary && (
          <section className="erp-section erp-break-avoid">
            <ReportRule />
            <h2 className="erp-section-title">Payroll Summary</h2>
            <PayrollSummaryCards summary={summary} variant="print" />
          </section>
        )}

        <PrintDetailSection detail={detail} options={options} />

        {totalPresentation && (
          <section className="erp-section erp-break-avoid">
            <ReportRule />
            <div className="erp-totals-wrap">
              <PayrollReportTotalSection
                reportModel={sectionModel}
                variant="print"
              />
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className={sectionClassName}>
      <ReportPreviewHeader
        {...REPORT_HEADER_SHELL.payroll}
        fields={header.fields}
      />

      {summary && (
        <>
          <ReportRule variant="preview" />
          <h2 className="er-section-title">Payroll Summary</h2>
          <PayrollSummaryCards summary={summary} variant="preview" />
        </>
      )}

      <PreviewDetailSection detail={detail} />

      {totalPresentation && (
        <>
          <ReportRule variant="preview" />
          <div className="er-totals-wrap">
            <PayrollReportTotalSection
              reportModel={sectionModel}
              variant="preview"
            />
          </div>
        </>
      )}
    </div>
  );
}
